import Section from '@components/atoms/Section';
import {
  Heading2,
  Heading3,
  Heading4,
  Paragraph,
} from '@components/atoms/Typography';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import COLORS from '@lib/constants/colors';
import airtableBases, { CarrierComparative } from '@lib/helpers/airtableBases';
import { GetStaticProps } from 'next';
import React from 'react';
import styled from 'styled-components';
import PrimaryButton from 'components/atoms/buttons/PrimaryButton';
import Link from 'next/link';
import ROUTES from '@lib/constants/routes';
import JoinSection from '@components/organisms/sections/JoinSection';
import GridBox from '@components/atoms/GridBox';
import CloseButton from '@components/atoms/buttons/CloseButton';
import { useRouter } from 'next/router';
import ReactModal from 'react-modal';
import { useModal } from '@lib/hooks/modal/useModal';
import GhostButton from '@components/atoms/buttons/GhostButton';
import HideCrisp from '@components/atoms/HideCrisp';
import AmpWrap from '@components/atoms/amp/AmpWrap';
import BREAKPOINTS from 'lib/constants/breakpoints';
import { onlyUnique } from 'helpers';

interface Props {
  carriers: CarrierComparative[];
  compareString: string;
}

const CompareBar = styled.div`
  box-sizing: border-box;
  display: flex;
  height: auto;
  left: 0;
  padding: 1.5rem;
  position: fixed;
  text-align: center;
  bottom: 0;
  visibility: visible;
  z-index: 101;
  box-shadow: 0 6px 20px 1px #777;
  width: 100%;
  background-color: #fff;
  justify-content: center;
  background: #f4f5f6;
`;

const CompareButton = styled.button`
  padding: 4px;
  border-radius: 4px;
  background-color: #e0e0e0;
  border: 0;
  height: 36px;
  margin: 0 8px;
  position: relative;
  width: 80px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    width: 60px;
  }
`;

const CompareLogo = styled.img`
  padding: 4px;
  border-radius: 4px;
  border: 0;
  height: 36px;
  margin: 0 8px;
  position: relative;
  width: 80px;
  background-color: #fff;
  object-fit: contain;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    width: 60px;
  }
`;

const BlockTitle = styled(Heading3)`
  margin-bottom: 16px;
`;

const StatPanel = styled.div<{ hideBg?: boolean }>`
  display: block;
  position: relative;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding: ${(props) => (props.hideBg ? '0' : '16px')};
  border-radius: 3px 3px 0 0;
  min-width: 170px;
  background-color: ${(props) => (props.hideBg ? 'transparent' : '#fff')};
  border: ${(props) => (props.hideBg ? 'none' : '1px solid #dddddd')};
  margin: 0 0 -1px;
  amp-img > img {
    object-fit: contain;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: 24px;
`;

function CompareBlock({
  carriers,
  infos,
  title,
  buttons = false,
}: {
  carriers: CarrierComparative[];
  infos: {
    carrierToLabel: (carrier: CarrierComparative) => string | undefined | null;
    label: string;
  }[];
  title: string;
  buttons?: boolean;
}) {
  return (
    <div style={{ marginBottom: 32 }}>
      <BlockTitle>{title}</BlockTitle>
      {infos.map((info) => (
        <Row key={info.label}>
          {carriers.map((carrier) => (
            <StatPanel key={carrier.slug}>
              <Heading4
                style={{ fontWeight: 'normal', marginBottom: 8, fontSize: 14 }}
              >
                {info.label}
              </Heading4>
              <Paragraph>{info.carrierToLabel(carrier) || '-'}</Paragraph>
            </StatPanel>
          ))}
        </Row>
      ))}
      {buttons && (
        <Row>
          {carriers.map((carrier) => (
            <StatPanel hideBg key={carrier.slug}>
              <Link passHref href={ROUTES.startForm()}>
                <a>
                  <PrimaryButton
                    isBlock
                    style={{ marginTop: 32 }}
                    key={carrier.slug}
                  >
                    Cotar Seguro
                  </PrimaryButton>
                </a>
              </Link>
            </StatPanel>
          ))}
        </Row>
      )}
    </div>
  );
}

const Page = ({ carriers = [], compareString = '' }: Props) => {
  const router = useRouter();
  const slugs = (router.query.compareString?.toString() || compareString)
    .split('-vs-')
    .filter(onlyUnique);

  const carriersToCompare = carriers
    .filter((c) => slugs.includes(c.slug))
    .sort(
      (a, b) =>
        slugs.findIndex((s) => s === a.slug) -
        slugs.findIndex((s) => s === b.slug),
    );

  const [showModal, hideModal] = useModal(
    ({ in: open }) => (
      <ReactModal
        isOpen={open}
        onRequestClose={hideModal}
        style={{
          content: {
            borderRadius: 5,
            border: 'none',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            // height: isSmall ? '95%' : '70%',
            maxWidth: 600,
            width: '100vw',
            padding: 32,
            height: 'fit-content',
            bottom: 'auto',
            borderBottomLeftRadius: 5,
            borderBottomRightRadius: 5,
          },
        }}
      >
        <Heading3 center>Escolha as seguradoras</Heading3>
        {carriers
          .filter((c) => !carriersToCompare.some((c2) => c2.slug === c.slug))
          .map((c) => (
            <GridBox key={c.slug} mt={[16]} flexDirection="column">
              <GhostButton
                onClick={() => {
                  hideModal();
                  return router.push(
                    ROUTES.carriers.compare([...carriersToCompare, c]),
                    undefined,
                    { shallow: true },
                  );
                }}
              >
                {c.nome}
              </GhostButton>
            </GridBox>
          ))}
      </ReactModal>
    ),
    [carriersToCompare],
  );

  if (!compareString) return <PageContainer loading />;

  return (
    <PageContainer
      customAmpPage
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      title={`Qual é melhor ${carriersToCompare
        .map((c) => c.nome)
        .join(' ou ')}`}
      metaDescription={`${carriersToCompare
        .map((c) => c.nome)
        .join(
          ' ou ',
        )}? Qual tem o melhor seguro auto? Compare cotações, descontos, coberturas, avaliações e muito mais de cada seguradora.`}
    >
      <HideCrisp />
      <DefaultHeader
        subtitle="Qual o melhor seguradora para carro ou moto? Compare as
              seguradoras de maneira rápida e fácil"
        title={<>Qual seguradora é melhor?</>}
        buttonText="Simule uma cotação"
      />
      <Section style={{ maxWidth: '100%', overflowX: 'scroll' }} pastel>
        <Row>
          {carriersToCompare.map((carrier) => (
            <StatPanel style={{ minHeight: 60 }} key={carrier.slug} hideBg>
              <Heading3 center style={{ marginBottom: 8 }}>
                {carrier.nome}
              </Heading3>
              {carrier.logo && (
                <AmpWrap
                  nonAmp={
                    <img
                      style={{
                        width: '80%',
                        display: 'block',
                        margin: '0 auto 0',
                        maxHeight: '100px',
                        objectFit: 'contain',
                      }}
                      src={carrier?.logo[0]?.url}
                      alt={`Logo seguradora ${carrier.nome}`}
                    />
                  }
                  ampOnly={
                    <amp-img
                      width="3"
                      height="2"
                      src={carrier?.logo[0]?.url}
                      alt={`Logo seguradora ${carrier.nome}`}
                      layout="fill"
                    />
                  }
                />
              )}
            </StatPanel>
          ))}
        </Row>
        <CompareBlock
          carriers={carriersToCompare}
          infos={[
            { label: 'Atuação', carrierToLabel: (carrier) => carrier.atuacao },
            {
              label: 'Nota reclame aqui',
              carrierToLabel: (carrier) => carrier?.notaReclameAqui?.toFixed(1),
            },
          ]}
          title=""
          buttons
        />

        <CompareBlock
          carriers={carriersToCompare}
          infos={[
            {
              label: 'Franquia',
              carrierToLabel: (carrier) =>
                carrier.franquia
                  ? 'De acordo com apólice contratada	'
                  : 'Não tem',
            },
            {
              label: 'Roubo e furto',
              carrierToLabel: (carrier) =>
                carrier.roubo ? 'Cobre' : 'Não cobre',
            },
            {
              label: 'Perdas parcial',
              carrierToLabel: (carrier) =>
                carrier.perdaParcial ? 'Cobre' : 'Não cobre',
            },
            {
              label: 'Perda total',
              carrierToLabel: (carrier) =>
                carrier.perdaTotal ? 'Cobre' : 'Não cobre',
            },
            {
              label: 'Terceiros',
              carrierToLabel: (carrier) => carrier.terceiros,
            },
          ]}
          title="Coberturas"
        />
        <CompareBlock
          title="Adicionais"
          carriers={carriersToCompare}
          infos={[
            {
              label: 'Assistências 24 horas',
              carrierToLabel: (carrier) => carrier.assistencias?.join(', '),
            },
            {
              label: 'Carro reserva',
              carrierToLabel: (carrier) =>
                carrier.carroReserva ? 'Tem' : 'Não tem',
            },
            {
              label: 'Cobertura para vidros',
              carrierToLabel: (carrier) => (carrier.vidros ? 'Tem' : 'Não tem'),
            },
          ]}
        />
        <CompareBlock
          title="Aceitacão"
          carriers={carriersToCompare}
          infos={[
            {
              label: 'Veículos antigos',
              carrierToLabel: (carrier) =>
                carrier.veiculosAntigos ? 'Aceita' : 'Não aceita',
            },
            {
              label: 'Uso comercial',
              carrierToLabel: (carrier) => carrier.usoComercial,
            },
          ]}
        />
        <CompareBlock
          title="Sobre o seguro"
          carriers={carriersToCompare}
          infos={[
            {
              label: 'Vigência',
              carrierToLabel: (carrier) => carrier.vigencia,
            },
            {
              label: 'Forma de pagamento',
              carrierToLabel: (carrier) => carrier.pagamento?.join(', '),
            },
            {
              label: 'Descontos',
              carrierToLabel: (carrier) => carrier.descontos || 'Não tem',
            },
          ]}
        />
      </Section>

      <RichTextSection>
        <Paragraph>
          Na hora de definir qual a melhor seguradora pra sua moto ou carro, é
          importante que você tenha em mente o que está buscando. Por isso, fica
          claro que a resposta será diferente para cada pessoa, não acha? Porém,
          é possível usar alguns critérios que tornam a avaliação mais objetiva.
        </Paragraph>
        <Paragraph>
          Foi a partir daí que elaboramos esta ferramenta. Nela, você consegue
          comparar qual a diferença entre as principais seguradoras do mercado.
        </Paragraph>
        {carriersToCompare.map((carrier) => (
          <div key={carrier.slug}>
            {carrier.hasPage ? (
              <Heading2>
                <Link passHref href={ROUTES.carriers.carrier(carrier.slug)}>
                  <a> {carrier.nome} é confiável?</a>
                </Link>
              </Heading2>
            ) : (
              <Heading2>{carrier.nome} é confiável?</Heading2>
            )}
            <Paragraph>{carrier.sobre}</Paragraph>
            <Heading3>A {carrier.nome} no Reclame Aqui</Heading3>
            <Paragraph>
              A nota {carrier.nome} no Reclame Aqui é{' '}
              <strong>{carrier?.notaReclameAqui?.toFixed(1)}</strong>,
              classificada como “
              {(carrier?.notaReclameAqui || 0) >= 8 ? 'Boa' : 'Média'}”. Em
              relação aos índices, temos:
            </Paragraph>
            <ul>
              <li>
                {((carrier.reclamacoesRespondidas || 0) * 100).toFixed(0)}% de
                reclamações respondidas;
              </li>
              <li>
                {((carrier.reclamacoesSolucionadas || 0) * 100).toFixed(0)}% de
                queixas solucionadas;
              </li>
              <li>
                {((carrier.voltariamAFazerNegocio || 0) * 100).toFixed(0)}% dos
                consumidores voltariam a fazer negócio;
              </li>
            </ul>
          </div>
        ))}
      </RichTextSection>
      <JoinSection
        buttonText="Simule Agora"
        title="Seguro de carro ou moto barato?"
        description="Aqui na Luna você consegue ver preços das melhores seguradoras de forma totalmente digital!"
      />
      <CompareBar>
        <AmpWrap
          ampOnly={
            <a href={ROUTES.carriers.comparative()}>
              <PrimaryButton>Ver outras seguradoras</PrimaryButton>
            </a>
          }
          nonAmp={
            <>
              {Array.from({ length: 4 }).map((_, index) => {
                const carrier: CarrierComparative | null =
                  carriersToCompare[index] || null;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <GridBox width={['auto']} alignItems="center" key={index}>
                    {carrier?.slug ? (
                      <div style={{ position: 'relative' }}>
                        {carrier?.logo && (
                          <CompareLogo
                            src={carrier?.logo[0]?.url}
                            alt={`Logo seguradora ${carrier?.nome}`}
                          />
                        )}
                        <CloseButton
                          style={{
                            padding: 0,
                            position: 'absolute',
                            top: -11,
                            right: -2,
                            height: 'auto',
                            backgroundColor: '#d5d9dc',
                            width: 'auto',
                          }}
                          onClick={() => {
                            router.push(
                              ROUTES.carriers.compare(
                                carriersToCompare.filter(
                                  (c) => c.slug !== carrier.slug,
                                ),
                              ),
                              undefined,
                              { shallow: true },
                            );
                          }}
                        />
                      </div>
                    ) : (
                      <CompareButton onClick={showModal} />
                    )}
                  </GridBox>
                );
              })}
            </>
          }
        />
      </CompareBar>
    </PageContainer>
  );
};

export async function getStaticPaths() {
  return {
    paths: ['/seguradoras/comparativo/tokio-vs-porto'],
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { compareString } = params as { compareString: string };

  const carriers = await airtableBases.getCarriers();

  return {
    props: { carriers, compareString },
    revalidate: 3600,
  };
};

export default Page;
